import type { PublicationModel } from "../../types/models";
import { camelToSnakeCase } from "../../utils/api";
import { toValidDate } from "../../utils/date";
import type { PublicationOperationCandidate, PublicationUpdateCandidate } from "./types";

export type SchedulingBatchRequest = {
  time_zone: string;
  expected_publications: Array<{
    id: string;
    project_id: string | null;
    account_id: string;
    provider: PublicationModel["provider"];
    status: PublicationModel["status"];
    scheduled_at: string;
    post_info: unknown;
  }>;
  creates: Array<{
    id: string;
    project_id: string;
    account_id: string;
    provider: PublicationModel["provider"];
    scheduled_at: string;
    post_info: unknown;
  }>;
  updates: Array<{
    id: string;
    scheduled_at: string;
    post_info: unknown;
    status: "scheduled" | "failed";
  }>;
  delete_ids: string[];
};

function requiredDate(value: Date | string | null | undefined): string {
  const date = toValidDate(value);
  if (!date) throw new Error("A post is missing its scheduled time. Choose a time before saving.");
  return date.toISOString();
}

/** Submit only the reviewed changes, with a baseline for affected rows. */
export function buildSchedulingBatchRequest({
  baselinePublications,
  creates,
  updates,
  deletes,
}: {
  baselinePublications: readonly PublicationModel[];
  creates: readonly PublicationOperationCandidate[];
  updates: readonly PublicationUpdateCandidate[];
  deletes: readonly PublicationModel[];
}): SchedulingBatchRequest {
  const affectedIds = new Set([...deletes.map(pub => pub.id), ...updates.map(({ persistedPublication }) => persistedPublication.id)]);
  const baseline = baselinePublications.filter(pub => affectedIds.has(pub.id));
  if (baseline.length !== affectedIds.size) throw new Error("A changed post is missing its baseline. Refresh before saving.");
  return {
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    expected_publications: baseline.map((pub) => ({
      id: pub.id,
      project_id: pub.projectId ?? null,
      account_id: pub.accountId,
      provider: pub.provider,
      status: pub.status,
      scheduled_at: requiredDate(pub.scheduledAt),
      post_info: camelToSnakeCase(pub.postInfo ?? null),
    })),
    creates: creates.map(({ publication, projectId, accountId, scheduledAt }) => ({
      id: publication.id,
      project_id: projectId,
      account_id: accountId,
      provider: publication.provider,
      scheduled_at: requiredDate(scheduledAt),
      post_info: camelToSnakeCase(publication.postInfo ?? null),
    })),
    updates: updates.map(({ publication, persistedPublication, scheduledAt }) => ({
      id: persistedPublication.id,
      scheduled_at: requiredDate(scheduledAt),
      post_info: camelToSnakeCase(publication.postInfo ?? null),
      status: publication.status === "failed" ? "failed" : "scheduled",
    })),
    delete_ids: deletes.map((pub) => pub.id),
  };
}

export class SchedulingBatchError extends Error {
  constructor(message: string, public status?: number, public errorCode?: string, public requiredPlan?: string) {
    super(message);
    this.name = "SchedulingBatchError";
  }
}

/** The one 409 that means "look again and save": the server's copy moved under
 *  the reviewed baseline. Every other 409 is a rule the server enforced
 *  (account disconnected, duplicate…) and its message is the only thing
 *  telling the user what to change — relabelling it sent people into a Save
 *  loop. The code decides; the text match covers an API without codes. */
export const STALE_SCHEDULE_MESSAGE = "Schedule updated. Review the remaining changes before saving.";

function isStaleConflict(status: number, error: { error_code?: string; message?: string }): boolean {
  if (status !== 409) return false;
  if (error.error_code) return error.error_code === "scheduling_session_stale";
  return /\bstale\b/i.test(error.message ?? "");
}

/** A 402's reasons, as the paywall reads them. */
export type PaymentRequired = { errorCode?: string; requiredPlan?: string };

/** Asks the batch's payment gate without saving: the 402's reasons, or null
 *  when the user may post. Throws when the gate can't answer (error, timeout). */
export async function fetchPublicationCreationPolicy(accessToken: string): Promise<PaymentRequired | null> {
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/integrations/publications/creation-policy`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: AbortSignal.timeout(8000),
  });
  if (response.status === 402) {
    const error = await response.json().catch(() => ({}));
    return { errorCode: error.error_code, requiredPlan: error.required_plan };
  }
  if (!response.ok) throw new Error(`Publication creation pre-flight failed: ${response.status}`);
  return null;
}

/** fetchPublicationCreationPolicy, where a gate that can't answer gives null,
 *  since the write still enforces it. */
export async function checkPublicationCreation(accessToken: string): Promise<PaymentRequired | null> {
  try {
    return await fetchPublicationCreationPolicy(accessToken);
  } catch (error) {
    console.error("Publication creation pre-flight failed", error);
    return null;
  }
}

export async function saveSchedulingBatch(request: SchedulingBatchRequest, accessToken: string): Promise<void> {
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/integrations/publications/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(request),
  });
  if (response.ok) return;
  const error = await response.json().catch(() => ({}));
  throw new SchedulingBatchError(
    response.status === 402 ? "Upgrade Plan"
      : isStaleConflict(response.status, error) ? STALE_SCHEDULE_MESSAGE
      : error.message || "Couldn't save the schedule.",
    response.status, error.error_code, error.required_plan,
  );
}
